import { RRule } from "rrule";
import { NotificationType } from "../../constants/notification-type.enum";
import { NotificationOffsetUnit } from "../../constants/notification-offset-unit.enum";
import { NotificationTrigger } from "../../constants/notification-trigger.enum";

export class BasicTrialNotificationTemplate {
  public id: string;
  public diaryFormId: string;
  public name: string;
  public rule: string;
  public frequency: string;
  public when: string;
  public emailNotificationEnabled: boolean;
  public appNotificationEnabled: boolean;
  public mandatoryNotification: boolean;
  public enabled: boolean;
  public notificationType: NotificationType;
  public offsetUnit: NotificationOffsetUnit;
  public offsetAmount: number;
  public afterEvent: boolean;
  public trigger: NotificationTrigger = NotificationTrigger.None;
  public ediaryMissedEntryCount: number;

  constructor(init?: Partial<BasicTrialNotificationTemplate>) {
    Object.assign(this, init);

    if (init) {
      if (init.notificationType)
        this.notificationType = init.notificationType as NotificationType;

      if (init.offsetUnit)
        this.offsetUnit = init.offsetUnit as NotificationOffsetUnit;
    }

    // Create 'when' human readable text
    if (this.notificationType === NotificationType.Ediary) {
      this.when = this.whenForEdiary();
    }

    if (this.notificationType === NotificationType.Visit) {
      this.when = this.whenForVisit();
    }
  }

  whenForEdiary(): string {
    if (this.trigger === NotificationTrigger.Frequency) {
      const { frequency, dayOfWeek, dayOfMonth, time } = this.parseRRule(this.rule);

      switch (frequency) {
        case 'Weekly':
          return `Every ${dayOfWeek} at ${time}`;
        case 'Monthly':
          return `${this.ordinal(dayOfMonth)} day of each month at ${time}`;
      }

      return `${dayOfWeek} at ${time}`;
    }

    if (this.trigger === NotificationTrigger.MissedEntries) {
      return `After ${this.ediaryMissedEntryCount} missed eDiary entries`;
    }

    if (this.trigger === NotificationTrigger.DiaryResponse) {
      return 'Diary response meets specified criteria';
    }

    return 'n/a';
  }

  whenForVisit(): string {
    if (this.notificationType === NotificationType.Ediary)
      return '';

    if (!this.offsetAmount || this.offsetUnit === undefined)
      return '';

    const unitMap = {
      [NotificationOffsetUnit.Minutes]: 'minute',
      [NotificationOffsetUnit.Hours]: 'hour',
      [NotificationOffsetUnit.Days]: 'day',
      [NotificationOffsetUnit.Weeks]: 'week'
    };

    const unit = unitMap[this.offsetUnit];
    const pluralUnit = this.offsetAmount === 1 ? unit : `${unit}s`;
    const timing = this.afterEvent ? 'after' : 'before';

    return `${this.offsetAmount} ${pluralUnit} ${timing} visit`;
  }

  private parseRRule(ruleString: string): { frequency: string; dayOfWeek: string; dayOfMonth: number; time: string } {
    if (!ruleString)
      return { frequency: '', dayOfWeek: '', dayOfMonth: null, time: '' };

    const rule = RRule.fromString(ruleString);
    const options = rule.options;

    const frequencyMap = {
      [RRule.WEEKLY]: 'Weekly',
      [RRule.MONTHLY]: 'Monthly'
    };
    const frequency = frequencyMap[options.freq] || 'Weekly';

    const dayMap = {
      [RRule.MO.weekday]: 'Mon',
      [RRule.TU.weekday]: 'Tue',
      [RRule.WE.weekday]: 'Wed',
      [RRule.TH.weekday]: 'Thu',
      [RRule.FR.weekday]: 'Fri',
      [RRule.SA.weekday]: 'Sat',
      [RRule.SU.weekday]: 'Sun'
    };
    const dayOfWeek = options.byweekday && options.byweekday.length > 0
      ? dayMap[options.byweekday[0]]
      : 'mon';

    const dayOfMonth = options.bymonthday && options.bymonthday.length > 0
      ? options.bymonthday[0]
      : null;

    const hour = options.byhour && options.byhour.length > 0 ? options.byhour[0] : 8;
    const minute = options.byminute && options.byminute.length > 0 ? options.byminute[0] : 0;
    const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

    return { frequency, dayOfWeek, dayOfMonth, time };
  }

  private ordinal(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }
}
